import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
  DoorOpen, Plus, Edit, Trash2, Save, X, Search, Bed,
  Building2, CheckCircle, AlertTriangle, RefreshCw,
  Wifi, QrCode, Tag, Wrench, ChevronDown
} from 'lucide-react';
import RoomService from '../../services/api/RoomService';
import WardService from '../../services/api/WardService';
import { useConfirm } from '../Common/ConfirmDialog';
import FacilityService from '../../services/api/FacilityService';
import { ROOM_TYPES, ROOM_STATUS, ROOM_AMENITIES } from '../../data/models/Room';

const EMPTY_FORM = {
  facilityId: '',
  wardId: '',
  roomNumber: '',
  roomName: '',
  floor: '1',
  wing: '',
  roomType: 'Single',
  status: 'available',
  totalBeds: 1,
  amenities: [],
  nfcTagId: '',
  qrCode: '',
  dailyRate: '',
  maintenanceNotes: ''
};

const RoomManagement = () => {
  const { state } = useAppContext();
  const confirm = useConfirm();
  const { selectedFacility } = state;

  const [rooms, setRooms] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [wards, setWards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterFacility, setFilterFacility] = useState(selectedFacility || 'all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWard, setFilterWard] = useState('all');
  const [occupancySummary, setOccupancySummary] = useState(null);

  const loadFacilities = useCallback(async () => {
    const result = await FacilityService.getAll();
    if (result.success) setFacilities(result.data || []);
  }, []);

  const loadWards = useCallback(async () => {
    const result = await WardService.getAll();
    if (result.success) setWards(result.data || []);
  }, []);

  const loadRooms = useCallback(async () => {
    setLoading(true);
    try {
      const result = await RoomService.getAll();
      if (result.success) setRooms(result.data || []);

      const summaryResult = await RoomService.getOccupancySummary(
        filterFacility !== 'all' ? filterFacility : null
      );
      if (summaryResult.success) setOccupancySummary(summaryResult.data);
    } catch (err) {
      setError('Failed to load rooms');
      console.error('Load rooms error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterFacility]);

  useEffect(() => { loadFacilities(); loadWards(); }, [loadFacilities, loadWards]);
  useEffect(() => { loadRooms(); }, [loadRooms]);

  const wardsByFacility = useMemo(() => {
    if (formData.facilityId) return wards.filter(w => w.facilityId === formData.facilityId);
    return wards;
  }, [wards, formData.facilityId]);

  const filteredRooms = useMemo(() => {
    return rooms.filter(r => {
      const matchesSearch = !searchQuery ||
        r.roomNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.roomName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFacility = filterFacility === 'all' || r.facilityId === filterFacility;
      const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
      const matchesWard = filterWard === 'all' || r.wardId === filterWard;
      return matchesSearch && matchesFacility && matchesStatus && matchesWard;
    });
  }, [rooms, searchQuery, filterFacility, filterStatus, filterWard]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const validate = () => {
    if (!formData.facilityId) return 'Please select a facility';
    if (!formData.wardId) return 'Please select a ward';
    if (!formData.roomNumber.trim()) return 'Room number is required';
    if (formData.totalBeds < 1 || formData.totalBeds > 10) return 'Beds must be between 1 and 10';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setSaving(true);
    setError(null);

    try {
      const bedCount = parseInt(formData.totalBeds) || 1;
      const beds = Array.from({ length: bedCount }, (_, i) => ({
        label: String.fromCharCode(65 + i),
        status: 'available'
      }));

      const payload = {
        ...formData,
        totalBeds: bedCount,
        beds,
        dailyRate: formData.dailyRate ? parseFloat(formData.dailyRate) : null
      };

      let result;
      if (editingId) {
        result = await RoomService.update(editingId, payload);
      } else {
        result = await RoomService.create(payload);
      }

      if (!result.success) throw new Error(result.error || 'Save failed');

      flash(editingId ? 'Room updated successfully' : 'Room created successfully');
      setShowForm(false);
      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
      loadRooms();
    } catch (err) {
      setError(err.message);
      console.error('Save room error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (room) => {
    setEditingId(room.id);
    setFormData({
      facilityId: room.facilityId || '',
      wardId: room.wardId || '',
      roomNumber: room.roomNumber || '',
      roomName: room.roomName || '',
      floor: room.floor || '1',
      wing: room.wing || '',
      roomType: room.roomType || 'Single',
      status: room.status || 'available',
      totalBeds: room.totalBeds || 1,
      amenities: room.amenities || [],
      nfcTagId: room.nfcTagId || '',
      qrCode: room.qrCode || '',
      dailyRate: room.dailyRate || '',
      maintenanceNotes: room.maintenanceNotes || ''
    });
    setShowForm(true);
    setError(null);
  };

  const handleDelete = async (roomId) => {
    if (!await confirm({ title: 'Delete Room', message: 'Delete this room? This cannot be undone.', confirmLabel: 'Delete', variant: 'danger' })) return;
    const result = await RoomService.delete(roomId);
    if (result.success) {
      flash('Room deleted');
      loadRooms();
    } else {
      setError(result.error || 'Delete failed');
    }
  };

  const getFacilityName = (id) => facilities.find(f => f.id === id)?.name || id;
  const getWardName = (id) => wards.find(w => w.id === id)?.name || id;

  const getStatusBadge = (status) => {
    const map = {
      available: 'bg-green-100 text-green-800',
      occupied: 'bg-blue-100 text-blue-800',
      reserved: 'bg-amber-100 text-amber-800',
      maintenance: 'bg-orange-100 text-orange-800',
      closed: 'bg-red-100 text-red-800'
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const getRoomTypeBadge = (type) => {
    const map = {
      Single: 'bg-indigo-100 text-indigo-800',
      Shared: 'bg-teal-100 text-teal-800',
      Premium: 'bg-purple-100 text-purple-800',
      'Dementia-specific': 'bg-rose-100 text-rose-800',
      Palliative: 'bg-slate-100 text-slate-800',
      Respite: 'bg-cyan-100 text-cyan-800',
      Isolation: 'bg-red-100 text-red-800'
    };
    return map[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3">
              Room & Bed Management
              <DoorOpen className="w-7 h-7 text-teal-300" />
            </h2>
            <p className="text-sm mt-1 opacity-90">
              Manage rooms, beds, and occupancy across facilities
            </p>
          </div>
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setFormData({ ...EMPTY_FORM, facilityId: filterFacility !== 'all' ? filterFacility : '' }); setError(null); }}
            className="bg-white text-teal-600 px-5 py-2.5 rounded-xl font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add Room
          </button>
        </div>
      </div>

      {/* Stats */}
      {occupancySummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-5 border border-teal-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-200 rounded-lg"><DoorOpen className="w-5 h-5 text-teal-700" /></div>
              <div>
                <h3 className="text-2xl font-bold text-teal-900">{occupancySummary.totalRooms}</h3>
                <p className="text-xs text-teal-600">Total Rooms</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-200 rounded-lg"><Bed className="w-5 h-5 text-blue-700" /></div>
              <div>
                <h3 className="text-2xl font-bold text-blue-900">{occupancySummary.totalBeds}</h3>
                <p className="text-xs text-blue-600">Total Beds</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-5 border border-green-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-200 rounded-lg"><CheckCircle className="w-5 h-5 text-green-700" /></div>
              <div>
                <h3 className="text-2xl font-bold text-green-900">{occupancySummary.availableBeds}</h3>
                <p className="text-xs text-green-600">Available Beds</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-2xl p-5 border border-indigo-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-200 rounded-lg"><Building2 className="w-5 h-5 text-indigo-700" /></div>
              <div>
                <h3 className="text-2xl font-bold text-indigo-900">{occupancySummary.occupiedBeds}</h3>
                <p className="text-xs text-indigo-600">Occupied Beds</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl p-5 border border-amber-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-200 rounded-lg"><Tag className="w-5 h-5 text-amber-700" /></div>
              <div>
                <h3 className="text-2xl font-bold text-amber-900">{occupancySummary.occupancyRate}%</h3>
                <p className="text-xs text-amber-600">Occupancy Rate</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">{successMsg}</p>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingId ? 'Edit Room' : 'Add New Room'}
            </h3>
            <button onClick={() => { setShowForm(false); setEditingId(null); }} className="p-2 hover:bg-gray-200 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Location */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-teal-600" /> Location
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Facility *</label>
                  <select value={formData.facilityId} onChange={(e) => { handleChange('facilityId', e.target.value); handleChange('wardId', ''); }} required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
                    <option value="">Select facility...</option>
                    {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ward / Unit *</label>
                  <select value={formData.wardId} onChange={(e) => handleChange('wardId', e.target.value)} required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    disabled={!formData.facilityId}>
                    <option value="">Select ward...</option>
                    {wardsByFacility.map(w => <option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Floor</label>
                  <input type="text" value={formData.floor} onChange={(e) => handleChange('floor', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="e.g. Ground, 1, 2" />
                </div>
              </div>
            </div>

            {/* Room Details */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DoorOpen className="w-4 h-4 text-teal-600" /> Room Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Number *</label>
                  <input type="text" value={formData.roomNumber} onChange={(e) => handleChange('roomNumber', e.target.value)} required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="e.g. 101" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Name</label>
                  <input type="text" value={formData.roomName} onChange={(e) => handleChange('roomName', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="e.g. Wattle Room" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                  <select value={formData.roomType} onChange={(e) => handleChange('roomType', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
                    {Object.values(ROOM_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Beds</label>
                  <input type="number" min="1" max="10" value={formData.totalBeds} onChange={(e) => handleChange('totalBeds', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={formData.status} onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
                    {Object.entries(ROOM_STATUS).map(([k, v]) => <option key={k} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Wing</label>
                  <input type="text" value={formData.wing} onChange={(e) => handleChange('wing', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="e.g. East Wing" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Daily Rate (AUD)</label>
                  <input type="number" step="0.01" value={formData.dailyRate} onChange={(e) => handleChange('dailyRate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="e.g. 85.00" />
                </div>
              </div>
            </div>

            {/* IoT Identifiers */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Wifi className="w-4 h-4 text-teal-600" /> IoT / Point-of-Care Identifiers
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NFC Tag ID</label>
                  <input type="text" value={formData.nfcTagId} onChange={(e) => handleChange('nfcTagId', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="Scan or enter NFC tag" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">QR Code</label>
                  <input type="text" value={formData.qrCode} onChange={(e) => handleChange('qrCode', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="QR code identifier" />
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-teal-600" /> Amenities
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {ROOM_AMENITIES.map(amenity => (
                  <label key={amenity} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors text-sm ${
                    formData.amenities.includes(amenity)
                      ? 'bg-teal-50 border-teal-300 text-teal-800'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-teal-200'
                  }`}>
                    <input type="checkbox" checked={formData.amenities.includes(amenity)} onChange={() => toggleAmenity(amenity)} className="sr-only" />
                    <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                      formData.amenities.includes(amenity) ? 'bg-teal-500 border-teal-500' : 'border-gray-300'
                    }`}>
                      {formData.amenities.includes(amenity) && <CheckCircle className="w-3 h-3 text-white" />}
                    </div>
                    {amenity}
                  </label>
                ))}
              </div>
            </div>

            {/* Maintenance Notes */}
            <div className="border-t border-gray-200 pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-teal-600" /> Notes
              </h4>
              <textarea value={formData.maintenanceNotes} onChange={(e) => handleChange('maintenanceNotes', e.target.value)}
                rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Any maintenance notes or special instructions..." />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-6 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 font-medium flex items-center gap-2 disabled:opacity-50">
                {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> {editingId ? 'Update Room' : 'Create Room'}</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by room number or name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
          </div>
          <select value={filterFacility} onChange={(e) => setFilterFacility(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
            <option value="all">All Facilities</option>
            {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select value={filterWard} onChange={(e) => setFilterWard(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
            <option value="all">All Wards</option>
            {wards.filter(w => filterFacility === 'all' || w.facilityId === filterFacility).map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
            <option value="all">All Statuses</option>
            {Object.values(ROOM_STATUS).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
      </div>

      {/* Room Table */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading rooms...</div>
        ) : filteredRooms.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <DoorOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-lg font-medium">No rooms found</p>
            <p className="text-sm">Add rooms using the button above</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs font-bold uppercase text-gray-600">
                <tr>
                  <th className="px-5 py-3">Room</th>
                  <th className="px-5 py-3">Facility</th>
                  <th className="px-5 py-3">Ward</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Beds</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Amenities</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRooms.map(room => (
                  <tr key={room.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div>
                        <p className="font-semibold text-gray-900">Room {room.roomNumber}</p>
                        {room.roomName && <p className="text-xs text-gray-500">{room.roomName}</p>}
                        {room.floor && <p className="text-xs text-gray-400">Floor {room.floor}{room.wing ? ` · ${room.wing}` : ''}</p>}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-600 text-xs">{getFacilityName(room.facilityId)}</td>
                    <td className="px-5 py-4 text-gray-600 text-xs">{getWardName(room.wardId)}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getRoomTypeBadge(room.roomType)}`}>
                        {room.roomType}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        <Bed className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium">
                          {room.occupiedBeds || 0}/{room.totalBeds || 1}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${getStatusBadge(room.status)}`}>
                        {room.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500">
                      {(room.amenities || []).length > 0
                        ? `${room.amenities.length} amenities`
                        : '—'}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(room)} title="Edit room"
                          className="p-2 hover:bg-teal-50 rounded-lg transition-colors">
                          <Edit className="w-4 h-4 text-teal-600" />
                        </button>
                        <button onClick={() => handleDelete(room.id)} title="Delete room"
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomManagement;
